import {
  Briefcase,
  Coins,
  FileSignature,
  Layers,
  type LucideIcon,
  Percent,
  ShoppingCart,
  Wallet,
} from "lucide-react";
import { FormProvider } from "react-hook-form";

import { AccountSection } from "./components/AccountSection";
import { AgreementsGroup } from "./components/AgreementsGroup";
import { DepositSection } from "./components/DepositSection";
import { FeeSection } from "./components/FeeSection";
import { ModelPortfolioSelector } from "./components/ModelPortfolioSelector";
import { OrdreModulSection } from "./components/OrdreModulSection";
import { PortfolioSelector } from "./components/PortfolioSelector";
import { useOrderForm } from "./hooks/useOrderForm";
import { QuerySecurity } from "@/api/queries/types";
import { Portfolio } from "@/api/types";
import { cn } from "@/lib/utils";

/**
 * Visual section card with icon + title header.
 * Replaces the flat `SectionDivider` rule to give the form a clearer
 * hierarchical structure and a more modern look.
 */
function FormCard({
  icon: Icon,
  title,
  description,
  className,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "rounded-xl border border-gray-200 dark:border-gray-700/70",
        "bg-white/60 dark:bg-gray-900/40 backdrop-blur-[1px]",
        "shadow-sm",
        className
      )}
    >
      <header className="flex items-start gap-3 px-4 sm:px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
            {title}
          </h3>
          {description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {description}
            </p>
          )}
        </div>
      </header>
      <div className="px-4 sm:px-5 py-4">{children}</div>
    </section>
  );
}

function buildExistData(portfolio: Portfolio | undefined) {
  if (!portfolio)
    return {
      isExisting: false,
    };
  const managementFee = portfolio.fees.find((f) => f.type === "Management");
  const karOkAccount = portfolio.accounts.find((a) =>
    a.tagsAsSet.includes("KAR-OK")
  );
  return {
    dpPortfolioId: String(portfolio.id),
    dpPortfolioName: portfolio.name,
    dpPortfolioShortName: portfolio.shortName ?? "",
    dpPortType: portfolio.typeCode ?? "",
    dpModelType:
      portfolio.modelPortfolio?.linkedPortfolio?.shortName ??
      portfolio.modelPortfolio?.code ??
      "",
    dpModelTypeName:
      portfolio.modelPortfolio?.linkedPortfolio?.name ??
      portfolio.modelPortfolio?.name ??
      "",
    dpAmountToInvest: 0,
    dpSumAvCash: portfolio.portfolioReport.accountBalance ?? 0,
    dpSumInsValue: portfolio.portfolioReport.positionMarketValue ?? 0,
    dpOnOffPercentage: "0",
    dpManagementFee:
      managementFee?.feePercent !== undefined
        ? String(managementFee.feePercent)
        : "1.5",
    dpFeeType: "all",
    dpOnOffAmount: 0,
    dpOnOffDate: "",
    dpAccount: karOkAccount?.number ?? "",
    dpNewAccount: "",
    dpWithdrawal: 0,
    ordreModul: "no",
    buyOrders: [],
    sellOrders: [],
    isExisting: true,
  };
}

type Props = {
  portfolios: Portfolio[];
  isCompany?: boolean;
  securities?: QuerySecurity[];
  initialData?: Record<string, unknown>;
  documentId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDocumentCreated?: (data: Record<string, any>, documentId?: string) => void;
  onClose?: () => void;
};

export function OrderForm({
  portfolios,
  isCompany = false,
  securities = [],
  initialData,
  documentId,
  onDocumentCreated,
  onClose,
}: Props) {
  const form = useOrderForm(initialData as Parameters<typeof useOrderForm>[0]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (data: Record<string, any>) => {
    // NOMODEL: sum of buy orders must match dpAmountToInvest
    if (data.dpModelType === "NOMODEL" && data.dpAmountToInvest > 0) {
      const buyRows = (data.buyOrders ?? []) as Array<{
        instrumentName?: string;
        amountInput?: string;
      }>;
      const sumBuyOrders = buyRows
        .filter((r) => r.instrumentName)
        .reduce((sum, r) => sum + (parseFloat(r.amountInput ?? "") || 0), 0);
      if (Math.abs(sumBuyOrders - data.dpAmountToInvest) > 0.01) {
        const fmt = (n: number) => n.toLocaleString("nb-NO");
        form.setError("buyOrders", {
          type: "manual",
          message: `Buy order total (${fmt(
            sumBuyOrders
          )}) must equal the investment amount (${fmt(data.dpAmountToInvest)})`,
        });
        return;
      }
      form.clearErrors("buyOrders");
    }

    const selectedPortfolio = portfolios.find(
      (p) => p.id === Number(data.dpPortfolioId)
    );
    onDocumentCreated?.(
      {
        existData: buildExistData(selectedPortfolio),
        newData: data,
      },
      documentId
    );
    onClose?.();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Compact sections: 2-column grid on large screens */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <FormCard
            icon={Briefcase}
            title="Portfolio"
            description="Select an existing portfolio or create a new one"
          >
            <PortfolioSelector
              portfolios={portfolios}
              allPortfolios={portfolios}
              isCompany={isCompany}
            />
          </FormCard>

          <FormCard
            icon={Layers}
            title="Model"
            description="Investment strategy template"
          >
            <ModelPortfolioSelector portfolios={portfolios} />
          </FormCard>

          <FormCard
            icon={Percent}
            title="Fees & Amount"
            description="Subscription, management fee and amount to invest"
          >
            <FeeSection portfolios={portfolios} />
          </FormCard>

          <FormCard
            icon={Coins}
            title="One-off"
            description="Optional one-time deposit"
          >
            <DepositSection />
          </FormCard>

          <FormCard
            icon={Wallet}
            title="Account & Withdrawal"
            description="Bank account and optional withdrawal"
            className="lg:col-span-2"
          >
            <AccountSection allPortfolios={portfolios} />
          </FormCard>
        </div>

        {/* Wide sections with tables — full width */}
        <FormCard
          icon={ShoppingCart}
          title="Order module"
          description="Specify buy and sell orders"
        >
          <OrdreModulSection portfolios={portfolios} securities={securities} />
        </FormCard>

        <FormCard
          icon={FileSignature}
          title="Agreements"
          description="Savings and withdrawal agreements"
        >
          <AgreementsGroup securities={securities} allPortfolios={portfolios} />
        </FormCard>

        {/* Footer actions — sticky to dialog bottom */}
        <div className="sticky bottom-0 -mx-4 px-4 py-3 mt-2 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:supports-[backdrop-filter]:bg-gray-900/80 flex justify-end gap-3 z-10">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="px-5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold shadow-sm transition-colors"
          >
            {documentId ? "Save changes" : "Add to document"}
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
